import { resolveShopImageUrl } from '@/features/shop/lib/shop-image'
import type { ShopProduct } from '@/features/shop/types/shop.types'
import { stripSearchHighlightMarkup } from '@/shared/utilities/search-highlight-markup'
import { pluralize } from '@/shared/utilities/pluralizer.utility'
import { pickResultDisplay } from './search-result.model'
import type { SearchEntityType, SearchResult } from '../types/search.types'

/**
 * One row of the public results page, whichever site it came from. Port of
 * the Lovable landing's `searchRanking.ts`: HubHive rows (Go) and Shop rows
 * (the Shop's public products API) are scored by the same lexical rule and
 * sorted together, so the "All" tab is one list rather than one block per
 * source.
 */
export type RankedSearchKind = 'business' | 'hive' | 'product' | 'service' | 'event' | 'person' | 'post'

export interface RankedSearchResult {
  id: string
  kind: RankedSearchKind
  title: string
  href: string
  imageUrls: string[]
  label: string
  metadata?: string
  snippet?: string
  /** Leaves this site (the Shop, ProFinder): rendered as a plain anchor. */
  external: boolean
  /** The business service the query hit, when it hit one. */
  matchedService?: string
  score: number
  sourceOrder: number
}

type Dict = Record<string, unknown>

const KIND_BY_ENTITY: Record<SearchEntityType, RankedSearchKind> = {
  business: 'business',
  hive: 'hive',
  event: 'event',
  post: 'post',
  product: 'product',
  service_funnel: 'service',
  user: 'person',
}

const LABEL_BY_KIND: Record<RankedSearchKind, string> = {
  business: 'Business',
  hive: 'Hive',
  product: 'Product',
  service: 'Service',
  event: 'Event',
  person: 'Person',
  post: 'Post',
}

export const SHOP_PRODUCT_LABEL = 'Shop product'

const normalize = (value: unknown) =>
  String(value ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
const tokens = (value: string) => normalize(value).split(' ').filter(Boolean)
const isAbsolute = (href: string) => /^https?:\/\//i.test(href)

export function relevanceScore(
  query: string,
  fields: { title: unknown; category?: unknown; description?: unknown; secondary?: unknown }
): number {
  const q = normalize(query)
  const title = normalize(fields.title)
  if (!q || !title) return 0
  const qTokens = tokens(q)
  const titleTokens = tokens(title)
  let score = 0
  if (title === q) score = 1000
  else if (title.startsWith(`${q} `) || title.includes(` ${q} `) || title.endsWith(` ${q}`)) score = 820
  else if (
    qTokens.length > 0 &&
    qTokens.every((token) => titleTokens.some((word) => word === token || word.startsWith(token)))
  )
    score = 720
  else if (title.includes(q)) score = 650
  else if (qTokens.some((token) => titleTokens.some((word) => word.startsWith(token)))) score = 500

  const category = normalize(fields.category)
  const secondary = normalize(fields.secondary)
  const description = normalize(fields.description)
  const allTokensInCategory =
    qTokens.length > 0 && qTokens.every((token) => `${category} ${secondary}`.includes(token))
  if (allTokensInCategory) score = Math.max(score, 390)
  else if (qTokens.some((token) => `${category} ${secondary}`.includes(token))) score = Math.max(score, 300)
  if (qTokens.length > 0 && qTokens.every((token) => description.includes(token))) score = Math.max(score, 140)
  else if (qTokens.some((token) => description.includes(token))) score = Math.max(score, 80)
  return score
}

function cleanText(value: unknown): string | undefined {
  const text =
    typeof value === 'string'
      ? stripSearchHighlightMarkup(value)
          .replace(/<[^>]*>/g, ' ')
          .replace(/&nbsp;/gi, ' ')
          .replace(/&amp;/gi, '&')
          .replace(/\s+/g, ' ')
          .trim()
      : ''
  return text || undefined
}

export function boundedSnippet(value: unknown, maxLength = 176): string | undefined {
  const text = cleanText(value)
  if (!text || text.length <= maxLength) return text
  const cut = text.slice(0, maxLength + 1)
  const boundary = cut.lastIndexOf(' ')
  return `${cut.slice(0, boundary > maxLength * 0.65 ? boundary : maxLength).trimEnd()}…`
}

const str = (data: Dict, key: string): string | undefined =>
  typeof data[key] === 'string' ? (data[key] as string) : undefined

/** A business's service names as one string — the Go row carries `service_funnel_names`. */
function serviceNames(data: Dict): string[] {
  const value = data.service_funnel_names ?? data.services
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'string'
          ? item
          : item && typeof item === 'object' && typeof (item as Dict).name === 'string'
            ? ((item as Dict).name as string)
            : ''
      )
      .map((name) => cleanText(name) ?? '')
      .filter(Boolean)
  }
  return typeof value === 'string' ? value.split(',').map((name) => name.trim()).filter(Boolean) : []
}

function matchingService(names: string[], query: string): string | undefined {
  const q = normalize(query)
  return (
    names.find((name) => normalize(name).includes(q) || q.includes(normalize(name))) ??
    names.find((name) => tokens(q).some((token) => normalize(name).includes(token)))
  )
}

function reviewText(data: Dict): string | undefined {
  const count = Number(data.review_count ?? 0)
  const rating = Number(data.rating ?? 0)
  return count > 0 && rating > 0 ? `${rating.toFixed(1)} · ${count} ${pluralize('review', count)}` : undefined
}

function locationText(data: Dict): string | undefined {
  const city = cleanText(data.city)
  const region = cleanText(data.state) ?? cleanText(data.region)
  if (city) return [city, region].filter(Boolean).join(', ')
  const address = cleanText(data.address)
  if (!address) return undefined
  const parts = address.split(',').map((part) => part.trim()).filter(Boolean)
  return parts.length >= 2 ? parts.slice(-2).join(', ') : address
}

/** Project one Go search row into a ranked row. */
export function rankHubRow(query: string, row: SearchResult, sourceOrder: number): RankedSearchResult {
  const display = pickResultDisplay(row)
  const data: Dict = row.data ?? {}
  const kind = KIND_BY_ENTITY[row.entity_type]
  const subtitle = display.subtitle ? cleanText(display.subtitle) : undefined
  const description = str(data, 'description') ?? str(data, 'summary') ?? str(data, 'text')
  const base = {
    id: String(row.identifier ?? `${row.entity_type}-${sourceOrder}`),
    kind,
    href: display.href,
    imageUrls: display.thumbnail ? [display.thumbnail] : [],
    label: LABEL_BY_KIND[kind],
    external: isAbsolute(display.href),
    sourceOrder,
  }
  const title = cleanText(display.title) ?? LABEL_BY_KIND[kind]

  switch (kind) {
    case 'business': {
      const names = serviceNames(data)
      const service = matchingService(names, query)
      const metadata = [service ?? locationText(data), service ? locationText(data) : undefined, reviewText(data)]
        .filter(Boolean)
        .join(' · ')
      return {
        ...base,
        title,
        metadata: metadata || undefined,
        snippet: boundedSnippet(str(data, 'summary') ?? str(data, 'description')),
        matchedService: service,
        score: relevanceScore(query, {
          title,
          category: str(data, 'category') ?? str(data, 'primary_category'),
          secondary: names.join(' '),
          description,
        }),
      }
    }
    case 'hive': {
      const members = Number(data.member_count ?? 0)
      const metadata = [
        data.join_policy === 'closed' ? 'Private' : 'Public',
        members > 0 ? `${members.toLocaleString()} ${pluralize('member', members)}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
      return {
        ...base,
        title,
        metadata: metadata || undefined,
        snippet: boundedSnippet(str(data, 'description')),
        score: relevanceScore(query, { title, category: str(data, 'category'), description }),
      }
    }
    case 'post': {
      const postTitle = cleanText(display.title)?.slice(0, 100) ?? 'Post'
      return {
        ...base,
        title: postTitle,
        metadata: [subtitle, cleanText(data.hive_name)].filter(Boolean).join(' · ') || undefined,
        score: relevanceScore(query, { title: postTitle, secondary: subtitle, description }),
      }
    }
    case 'event':
      return {
        ...base,
        title,
        metadata: [subtitle, locationText(data) ?? cleanText(data.location)].filter(Boolean).join(' · ') || undefined,
        score: relevanceScore(query, { title, category: cleanText(data.location), description }),
      }
    case 'person':
      return { ...base, title, score: relevanceScore(query, { title }) }
    case 'service':
      return {
        ...base,
        title,
        metadata: subtitle,
        score: relevanceScore(query, { title, category: subtitle, description }),
      }
    case 'product':
    default:
      return {
        ...base,
        title,
        metadata: subtitle,
        snippet: boundedSnippet(description),
        score: relevanceScore(query, { title, category: subtitle, description }),
      }
  }
}

/** Project one Shop product into a ranked row that leaves for the Shop. */
export function rankShopProduct(query: string, product: ShopProduct, sourceOrder: number): RankedSearchResult {
  const variant = cleanText(product.variantLabel ?? product.variantFamilyName)
  return {
    id: product.id,
    kind: 'product',
    title: product.title,
    href: product.productUrl,
    imageUrls: product.images
      .map((image) => resolveShopImageUrl(image))
      .filter((image): image is string => Boolean(image)),
    label: SHOP_PRODUCT_LABEL,
    metadata: [product.business?.name, variant].filter(Boolean).join(' · ') || undefined,
    snippet: boundedSnippet(product.description),
    external: true,
    score: relevanceScore(query, {
      title: product.title,
      category: variant,
      secondary: product.business?.name,
      description: product.description,
    }),
    sourceOrder,
  }
}

/** HubHive rows and Shop rows, scored by one rule and sorted together. */
export function rankSearchResults(
  query: string,
  hubRows: readonly SearchResult[],
  shopProducts: readonly ShopProduct[]
): RankedSearchResult[] {
  const rows: RankedSearchResult[] = []
  for (const row of hubRows) rows.push(rankHubRow(query, row, rows.length))
  for (const product of shopProducts) rows.push(rankShopProduct(query, product, rows.length))
  return rows
    .map((row) => ({ ...row, score: Math.max(row.score, 1) }))
    .sort(
      (a, b) =>
        b.score - a.score ||
        (a.score === 1 ? a.sourceOrder - b.sourceOrder : a.title.localeCompare(b.title)) ||
        `${a.kind}:${a.id}`.localeCompare(`${b.kind}:${b.id}`)
    )
}

export function isExactEntityMatch(result: RankedSearchResult, query: string): boolean {
  return normalize(result.title) === normalize(query)
}

const STRONG_SERVICE_WORDS = new Set([
  'landscaping',
  'landscape',
  'plumbing',
  'roofing',
  'electrician',
  'electrical',
  'cleaning',
  'flooring',
  'painting',
  'moving',
  'mowing',
  'hvac',
  'repair',
  'installation',
  'remodeling',
])

/** A one-word query that is a trade, not a name — the "find pros" case. */
export function isStrongServiceQuery(query: string): boolean {
  return STRONG_SERVICE_WORDS.has(normalize(query))
}

export function hasGenuineServiceRelevance(result: RankedSearchResult, query: string): boolean {
  if (result.kind !== 'business') return false
  return Boolean(result.matchedService) || result.score >= 300
}
